import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { API, useAuth } from '../App';
import DashboardLayout from '../components/layouts/DashboardLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import {
  Package, Plus, ArrowLeft, Upload, X, DollarSign, Globe,
  Check, Loader2, Image as ImageIcon, Trash2, Edit, Eye
} from 'lucide-react';

const categories = [
  { id: 'agriculture', name: 'Agriculture & Raw Materials' },
  { id: 'agro-processing', name: 'Agro-Processing & Food' },
  { id: 'manufacturing', name: 'Manufacturing & Industrial' },
  { id: 'textiles', name: 'Textiles & Garments' },
  { id: 'pharmaceuticals', name: 'Pharmaceuticals & Healthcare' },
  { id: 'construction', name: 'Construction Materials' },
  { id: 'digital-services', name: 'Digital Services' },
  { id: 'creative-industries', name: 'Creative Industries & Crafts' },
  { id: 'mining', name: 'Mining & Minerals' },
  { id: 'energy', name: 'Energy & Renewables' },
  { id: 'other', name: 'Other' }
];

const currencies = ['USD', 'EUR', 'GBP', 'NGN', 'KES', 'ZAR', 'EGP', 'GHS', 'XOF', 'XAF'];

const africanCountries = [
  'Nigeria', 'Ghana', 'Kenya', 'South Africa', 'Egypt', 'Morocco', 'Tanzania',
  'Ethiopia', 'Rwanda', 'Senegal', "Côte d'Ivoire", 'Cameroon', 'Uganda',
  'Zimbabwe', 'Botswana', 'Namibia', 'Tunisia', 'Algeria', 'Angola', 'Benin',
  'Burkina Faso', 'Mali', 'Niger', 'Togo', 'DR Congo', 'Zambia', 'Mozambique',
  'Madagascar', 'Mauritius', 'Malawi', 'Libya', 'Sudan', 'Somalia'
];

const certifications = [
  'ISO 9001', 'ISO 22000', 'HACCP', 'Organic Certified', 'Fair Trade',
  'NAFDAC Approved', 'SON Certified', 'KEBS Certified', 'SABS Approved',
  'CE Marking', 'FDA Approved', 'Halal Certified', 'Kosher Certified'
];

const ProductListingPage = () => {
  const navigate = useNavigate();
  const { productId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [myProducts, setMyProducts] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [editMode, setEditMode] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    category: '',
    hs_code: '',
    price: '',
    currency: 'USD',
    moq: '1',
    production_capacity: '',
    country_of_origin: '',
    certifications: [],
    delivery_markets: [],
    images: []
  });

  // Fetch products
  const fetchMyProducts = useCallback(async () => {
    const token = localStorage.getItem('atff_token');
    if (!token) {
      setLoading(false);
      return;
    }
    
    setLoading(true);
    try {
      const response = await axios.get(`${API}/my-products`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMyProducts(response.data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
      setMyProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchMyProducts();
  }, [fetchMyProducts]);

  useEffect(() => {
    if (productId) {
      loadProductForEdit(productId);
    }
  }, [productId]);

  const loadProductForEdit = async (id) => {
    try {
      const token = localStorage.getItem('atff_token');
      const response = await axios.get(`${API}/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const product = response.data;
      setFormData({
        name: product.name || '',
        description: product.description || '',
        category: product.category || '',
        hs_code: product.hs_code || '',
        price: product.price?.toString() || '',
        currency: product.currency || 'USD',
        moq: product.moq?.toString() || '1',
        production_capacity: product.production_capacity || '',
        country_of_origin: product.country_of_origin || '',
        certifications: product.certifications || [],
        delivery_markets: product.delivery_markets || [],
        images: product.images || []
      });
      setEditMode(true);
      setShowForm(true);
    } catch (error) {
      toast.error('Failed to load product');
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (name, value) => {
    setFormData({ ...formData, [name]: value });
  };

  const handleCertificationToggle = (cert) => {
    const current = formData.certifications;
    if (current.includes(cert)) {
      setFormData({ ...formData, certifications: current.filter(c => c !== cert) });
    } else {
      setFormData({ ...formData, certifications: [...current, cert] });
    }
  };

  const handleMarketToggle = (country) => {
    const current = formData.delivery_markets;
    if (current.includes(country)) {
      setFormData({ ...formData, delivery_markets: current.filter(c => c !== country) });
    } else {
      setFormData({ ...formData, delivery_markets: [...current, country] });
    }
  };

  const handleImageAdd = (url) => {
    if (url && !formData.images.includes(url)) {
      setFormData({ ...formData, images: [...formData.images, url] });
    }
  };

  const handleImageRemove = (url) => {
    setFormData({ ...formData, images: formData.images.filter(i => i !== url) });
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image too large. Maximum size is 5MB');
      return;
    }
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Invalid image type. Please use JPG, PNG, WebP, or GIF');
      return;
    }
    
    setUploadingImage(true);
    try {
      const token = localStorage.getItem('atff_token');
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      
      const response = await axios.post(`${API}/products/upload-image`, formDataUpload, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      if (response.data.image_url) {
        // Convert relative URL to full URL
        const fullUrl = response.data.image_url.startsWith('http') 
          ? response.data.image_url 
          : `${API.replace('/api', '')}${response.data.image_url}`;
        handleImageAdd(fullUrl);
        toast.success('Image uploaded successfully');
      }
    } catch (error) {
      console.error('Image upload error:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploadingImage(false);
      e.target.value = ''; // Reset file input
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      category: '',
      hs_code: '',
      price: '',
      currency: 'USD',
      moq: '1',
      production_capacity: '',
      country_of_origin: '',
      certifications: [],
      delivery_markets: [],
      images: []
    });
    setEditMode(false);
    setShowForm(false);
  };

  const handleSubmit = async () => {
    if (!formData.name || !formData.description || !formData.category || !formData.price || !formData.country_of_origin) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSaving(true);
    try {
      const token = localStorage.getItem('atff_token');
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        moq: parseInt(formData.moq) || 1
      };

      if (editMode && productId) {
        await axios.put(`${API}/products/${productId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product updated successfully!');
      } else {
        await axios.post(`${API}/products`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Product listed successfully!');
      }

      resetForm();
      fetchMyProducts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save product');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;

    try {
      const token = localStorage.getItem('atff_token');
      await axios.delete(`${API}/products/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Product deleted');
      fetchMyProducts();
    } catch (error) {
      toast.error('Failed to delete product');
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <Loader2 className="w-8 h-8 animate-spin text-continental" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-charcoal">My Products</h1>
            <p className="text-gray-600">Manage your product listings on ATFFtrade.africa</p>
          </div>
          {!showForm && (
            <Button onClick={() => setShowForm(true)} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Add New Product
            </Button>
          )}
        </div>

        {showForm ? (
          /* Product Form */
          <Card className="mb-6">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>{editMode ? 'Edit Product' : 'Add New Product'}</CardTitle>
                  <CardDescription>Fill in the details to list your product on the marketplace</CardDescription>
                </div>
                <Button variant="ghost" onClick={resetForm}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Info */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Product Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g., Premium Cocoa Beans"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select value={formData.category} onValueChange={(v) => handleSelectChange('category', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map(cat => (
                        <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="description">Description *</Label>
                <Textarea
                  id="description"
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe your product, quality, specifications..."
                  rows={4}
                />
              </div>

              {/* Pricing */}
              <div className="grid md:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="price">Price *</Label>
                  <div className="relative">
                    <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      id="price"
                      name="price"
                      type="number"
                      step="0.01"
                      value={formData.price}
                      onChange={handleChange}
                      placeholder="0.00"
                      className="pl-8"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={formData.currency} onValueChange={(v) => handleSelectChange('currency', v)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {currencies.map(cur => (
                        <SelectItem key={cur} value={cur}>{cur}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="moq">Minimum Order Qty</Label>
                  <Input
                    id="moq"
                    name="moq"
                    type="number"
                    value={formData.moq}
                    onChange={handleChange}
                    placeholder="1"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="hs_code">HS Code</Label>
                  <Input
                    id="hs_code"
                    name="hs_code"
                    value={formData.hs_code}
                    onChange={handleChange}
                    placeholder="e.g., 1801.00"
                  />
                </div>
              </div>

              {/* Origin & Capacity */}
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Country of Origin *</Label>
                  <Select value={formData.country_of_origin} onValueChange={(v) => handleSelectChange('country_of_origin', v)}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      {africanCountries.map(country => (
                        <SelectItem key={country} value={country}>{country}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="production_capacity">Production Capacity</Label>
                  <Input
                    id="production_capacity"
                    name="production_capacity"
                    value={formData.production_capacity}
                    onChange={handleChange}
                    placeholder="e.g., 500 tons/month"
                  />
                </div>
              </div>

              {/* Certifications */}
              <div className="space-y-2">
                <Label>Certifications</Label>
                <div className="flex flex-wrap gap-2">
                  {certifications.map(cert => (
                    <Badge
                      key={cert}
                      variant={formData.certifications.includes(cert) ? 'default' : 'outline'}
                      className={`cursor-pointer transition-colors ${
                        formData.certifications.includes(cert) 
                          ? 'bg-continental hover:bg-continental/90' 
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => handleCertificationToggle(cert)}
                    >
                      {formData.certifications.includes(cert) && <Check className="w-3 h-3 mr-1" />}
                      {cert}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Delivery Markets */}
              <div className="space-y-2">
                <Label>Available Delivery Markets</Label>
                <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto p-2 border rounded-lg">
                  {africanCountries.slice(0, 20).map(country => (
                    <Badge
                      key={country}
                      variant={formData.delivery_markets.includes(country) ? 'default' : 'outline'}
                      className={`cursor-pointer transition-colors ${
                        formData.delivery_markets.includes(country) 
                          ? 'bg-blue-600 hover:bg-blue-700' 
                          : 'hover:bg-gray-100'
                      }`}
                      onClick={() => handleMarketToggle(country)}
                    >
                      {country}
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Images */}
              <div className="space-y-2">
                <Label>Product Images</Label>
                <div className="flex flex-wrap gap-3">
                  {formData.images.map((img) => (
                    <div key={img} className="relative w-24 h-24 border rounded-lg overflow-hidden group">
                      <img src={img} alt="Product" className="w-full h-full object-cover" />
                      <button
                        onClick={() => handleImageRemove(img)}
                        className="absolute top-1 right-1 w-6 h-6 bg-red-500 text-white rounded-full opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <label className="relative w-24 h-24 border-2 border-dashed rounded-lg flex flex-col items-center justify-center text-gray-400 hover:border-continental hover:text-continental cursor-pointer transition-colors">
                    {uploadingImage ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <>
                        <Upload className="w-6 h-6 mb-1" />
                        <span className="text-xs text-center">Upload Image</span>
                      </>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      className="absolute inset-0 opacity-0 cursor-pointer"
                      onChange={handleImageUpload}
                      disabled={uploadingImage}
                    />
                  </label>
                </div>
                <p className="text-xs text-gray-500">Click to upload product images (JPG, PNG, WebP). Max 5MB per image.</p>
              </div>

              {/* Submit */}
              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button variant="outline" onClick={resetForm}>Cancel</Button>
                <Button onClick={handleSubmit} disabled={saving} className="btn-primary">
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Check className="w-4 h-4 mr-2" />
                      {editMode ? 'Update Product' : 'List Product'}
                    </>
                  )}
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Products List */}
        {!showForm && (
          <>
            {myProducts.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-700 mb-2">No products yet</h3>
                  <p className="text-gray-500 mb-4">
                    Start listing your products to reach buyers across Africa
                  </p>
                  <Button onClick={() => setShowForm(true)} className="btn-primary">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Your First Product
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {myProducts.map(product => (
                  <Card key={product.product_id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      {product.images?.[0] ? (
                        <img
                          src={product.images[0]}
                          alt={product.name}
                          className="w-full h-40 object-cover rounded-lg mb-3"
                        />
                      ) : (
                        <div className="w-full h-40 bg-gray-100 rounded-lg mb-3 flex items-center justify-center">
                          <Package className="w-12 h-12 text-gray-300" />
                        </div>
                      )}
                      <h3 className="font-semibold text-charcoal mb-1 line-clamp-1">{product.name}</h3>
                      <p className="text-sm text-gray-500 mb-2 line-clamp-2">{product.description}</p>
                      <div className="flex items-center justify-between mb-3">
                        <span className="text-lg font-bold text-continental">
                          {product.currency} {product.price?.toLocaleString()}
                        </span>
                        <Badge className={`text-xs ${
                          product.verification_status === 'verified' 
                            ? 'bg-green-100 text-green-700' 
                            : 'bg-yellow-100 text-yellow-700'
                        }`}>
                          {product.verification_status}
                        </Badge>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => navigate(`/products/${product.product_id}`)}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => {
                            navigate(`/products/edit/${product.product_id}`);
                            loadProductForEdit(product.product_id);
                          }}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Edit
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(product.product_id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProductListingPage;
